import { useRef, useLayoutEffect } from 'react';

/** \n → <br> HTML 변환 */
const textToHtml = (text) =>
  (text == null ? '' : String(text))
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');

/**
 * contenteditable DOM → 텍스트(\n) 추출.
 * <br>을 \n으로, 블록 요소(div/p) 전환 시 \n 삽입.
 * 브라우저가 자동으로 추가하는 마지막 \n은 제거.
 */
const htmlToText = (el) => {
  let out = '';
  const walk = (node) => {
    if (node.nodeType === 3 /* TEXT */) {
      out += node.textContent;
    } else if (node.nodeName === 'BR') {
      out += '\n';
    } else if (node.nodeType === 1 /* ELEMENT */) {
      const block = /^(DIV|P|BLOCKQUOTE|LI|H[1-6])$/.test(node.nodeName);
      if (block && out.length > 0 && !out.endsWith('\n')) out += '\n';
      node.childNodes.forEach(walk);
    }
  };
  el.childNodes.forEach(walk);
  return out.replace(/\n$/, ''); // 브라우저가 끝에 추가하는 여분 \n 제거
};

/**
 * 인라인 편집 텍스트(contentEditable).
 *
 * - 초기값·외부 변경은 innerHTML(<br> 방식)으로 주입한다.
 * - Enter 키 → <br> 직접 삽입(블록 생성 방지). 한글 IME 조합 중에는 무시.
 * - blur 시 DOM 순회로 \n을 추출해 onChange 호출.
 */
export default function Editable({ as = 'span', value, className = '', onChange, ...rest }) {
  const Tag = as;
  const elRef = useRef(null);
  const initialized = useRef(false);
  const committed = useRef(''); // 마지막으로 확정된 텍스트값 (blur 시 비교용)

  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const next = value == null ? '' : String(value);
    if (!initialized.current) {
      el.innerHTML = textToHtml(next);
      committed.current = next;
      initialized.current = true;
      return;
    }
    // 편집 중(포커스)에는 덮어쓰지 않음
    if (document.activeElement !== el && htmlToText(el) !== next) {
      el.innerHTML = textToHtml(next);
      committed.current = next;
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // IME 조합 중 Enter는 한 글자 완성용 — 줄바꿈으로 처리하지 않음
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      // eslint-disable-next-line no-restricted-globals
      document.execCommand('insertLineBreak');
    }
  };

  return (
    <Tag
      ref={elRef}
      className={`editable ${className}`.trim()}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onKeyDown={handleKeyDown}
      onBlur={(e) => {
        const current = htmlToText(e.currentTarget);
        // 실제로 텍스트가 바뀐 경우에만 onChange 호출 → 클릭만으로 dirty 되는 현상 방지
        if (onChange && current !== committed.current) {
          committed.current = current;
          onChange(current);
        }
      }}
      {...rest}
    />
  );
}
